import {
    RiArrowLeftRightFill,
    RiInboxArchiveLine, RiMoreFill,
} from "@remixicon/react";
import React from "react";
import { TicketItem, useTicketState } from "../../../contexts/ticket-state";
import Button from "../../theme/button/Button";
import Styles from "./Chat.module.scss";
import { ChatStatus, DashboardTicketDisplay, TicketPriority } from "../../../__generated__/api";
import { ActionContext, ActionHandler, ActionType } from "../../../contexts/ticket-state/action-registry";
import Integrations from "../../agent/integrations/Integrations";
import { LoadingIcon } from "../../loading/Loading";
import ColumnPicker from "../../pickers/ticket-states/ColumnPicker";
import Dropdown from "../../theme/dropdown/Dropdown";
import UserDisplay from "../labels/user/User";
import Conversation from "./conversation/Conversation";
import usePortal from "../../../contexts/PortalContext";
import ChatViewSidebar from "./ChatViewSidebar";
import { LoggingHandler } from "../../../contexts/logging/LoggingContext";

const FEATURE_FILL_FORM = "frontend.actions.prefill.form";

export interface ChatViewProps {
    ticket: TicketItem | undefined;
    onClose: () => void;
    onToggleDisplay: () => void;
    displayType: DashboardTicketDisplay;
    isReadOnly?: boolean;
    isViewPrivate?: boolean;
    metricPathSection: string;
}
export default function ChatView({
    ticket,
    onClose,
    onToggleDisplay,
    displayType,
    isReadOnly = false,
    isViewPrivate,
    metricPathSection,
}: ChatViewProps) {
    const ticketContext = useTicketState();
    const { featureFlags } = usePortal();

    if (!ticket) {
        return <div className={Styles.loading}><LoadingIcon /></div>;
    }
    ticket.useUpdates();
    const { chat } = ticket;
    const ticketCreator = ticket.requester;
    return (
        <div className={Styles.chat}>
            <div className={Styles["chat-panel"]}>
                <div className={Styles["chat-header"]}>
                    <div className={Styles["chat-description"]}>
                        <div className="chat-user-row">
                            <div className="ticket-number">
                                #
                                {chat.ticketNumber}
                            </div>
                            <div className="requester">
                                <UserDisplay
                                    user={ticketCreator}
                                    variant="xs"
                                    allowOverlay={!isReadOnly}
                                />
                            </div>
                            <div className="company">
                                {ticketCreator?.organization?.name ?? ticketCreator?.group}
                            </div>
                        </div>
                        <div className="subject">
                            {chat.subject}
                        </div>
                    </div>

                    {
                        !isReadOnly && (

                            <div className={Styles["chat-actions"]} data-tour-panel="chat-actions">

                                <Dropdown drop="down">
                                    <Dropdown.Toggle
                                        variant="secondary"
                                        size="small"
                                        prefixIcon={<RiMoreFill />}
                                        title="More Ticket Actions"
                                    />
                                    <Dropdown.Menu>
                                        {ticketContext
                                            .selectionState
                                            ?.getPossibleActions(ActionContext.TICKET_ACTION, [ticket])
                                            .map(({ button, handler }) => {
                                                const path = [
                                                    metricPathSection,
                                                    "chat",
                                                    "action",
                                                    button.text?.toLowerCase().replace(/ /g, "-"),
                                                ];

                                                return (
                                                    <Dropdown.Item
                                                        key={button.text}
                                                        prefixIcon={button.icon}
                                                        metricPath={path}
                                                        onClick={() => {
                                                            const container = new ActionHandler(
                                                                [ticket],
                                                                ActionContext.TICKET_ACTION,
                                                                () => {
                                                                    ticketContext.selectionState.update();
                                                                    LoggingHandler.instance.metric([...path, "done"]);
                                                                },
                                                                () => {
                                                                //
                                                                },
                                                            );
                                                            handler.handleBulkAction(container);
                                                        }}
                                                    >
                                                        {button.text}
                                                    </Dropdown.Item>
                                                );
                                            })}
                                    </Dropdown.Menu>
                                </Dropdown>
                                <Integrations
                                    title="Ticket Actions and Integrations"
                                    chatId={ticket.id}
                                    hasStarted={
                                        ticket.status !== ChatStatus.ChatRequested && ticket.status !== ChatStatus.Connected
                                    }
                                    shouldIncludeServices
                                    metricPathPrefix={[
                                        metricPathSection,
                                        "chat",
                                        "integrations",
                                    ]}
                                    className={Styles.integrations}
                                    ticket={ticket.chat}
                                    text={ticket.ticket.subject as string}
                                    ticketInput={featureFlags.has(FEATURE_FILL_FORM) ? {
                                        subject: ticket.ticket.subject as string,
                                        description: ticket.ticket.description,
                                        priority: ticket.ticket.priority as TicketPriority,
                                        requestorId: ticket.requester?.id,
                                        tags: ticket.ticket.tags,
                                        customFields: ticket.ticket.customFields?.map(field => ({
                                            ...field.value,
                                            formFieldId: field.formFieldId,
                                            type: field.type,
                                        })),
                                    } : undefined}
                                />
                                <Dropdown drop="down">
                                    <Dropdown.Toggle
                                        title="Move Ticket"
                                        variant="secondary"
                                        size="small"
                                        prefixIcon={<RiArrowLeftRightFill />}
                                        metricPath={[
                                            metricPathSection,
                                            "chat",
                                            "action",
                                            "move-ticket-menu",
                                        ]}
                                    />
                                    <Dropdown.Menu className="p-0">
                                        <ColumnPicker
                                            value={ticketContext.columnCache.getForTicket(ticket)?.id}
                                            onSelected={targetId => {
                                                const column = ticketContext.dashboardView?.getColumn(targetId);
                                                if (!column) {
                                                    return;
                                                }
                                                ticketContext.dashboardView?.moveTickets(
                                                    [ticket],
                                                    column,
                                                    ActionContext.TICKET_ACTION,
                                                    () => {
                                                        ticket.highlight();
                                                    },
                                                );
                                                LoggingHandler.instance.metric([
                                                    metricPathSection,
                                                    "chat",
                                                    "action",
                                                    "move-ticket",
                                                ]);
                                            }}
                                        />
                                    </Dropdown.Menu>
                                </Dropdown>
                                <Button
                                    title="Close Ticket"
                                    variant="danger"
                                    size="small"
                                    prefixIcon={<RiInboxArchiveLine />}
                                    metricPath={[
                                        metricPathSection,
                                        "chat",
                                        "action",
                                        "end-chat",
                                    ]}
                                    onClick={() => {
                                        ticketContext.selectionState.execute(
                                            ActionType.CLOSE,
                                            [ticket],
                                            undefined,
                                            LoggingHandler.instance.wrapMetric([
                                                metricPathSection,
                                                "chat",
                                                "action",
                                                "end-chat",
                                                "done",
                                            ]),
                                        );
                                    }}
                                />
                            </div>
                        )
                    }
                </div>
                <Conversation ticket={ticket} isReadOnly={isReadOnly} isViewPrivate={isViewPrivate ?? false} />
            </div>
            <ChatViewSidebar
                ticket={ticket}
                onClose={onClose}
                onToggleDisplay={isReadOnly ? undefined : onToggleDisplay}
                displayType={displayType}
                isReadOnly={isReadOnly}
                metricPathSection={metricPathSection}
            />
        </div>
    );
}

ChatView.defaultProps = {
    isReadOnly: false,
    isViewPrivate: false,
};
